import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import css from './ValueSourceSection.css';
import { TextField, Button } from '../../components/widgets';
import CollapsedPanelValues from '../../components/widgets/CollapsedPanelValues';
import sharedCss from './styles.css';
import toaster from '../../utils/toaster';
import { IconSizes } from '../../components/foundations/Icon';
import ImproveActions from './ImproveActions';
import {
    CustomAIDimensionContext,
    MAX_VALUES_LIMIT,
    MAX_OPTIONS_LIMIT,
    FORM_TRANSLATIONS,
    TOASTER_TRANSLATIONS,
    IMPROVE_BUTTON_TEXT,
    AUTO_SUGGEST_BUTTON_TEXT,
} from '../consts';

function ValueSourceSection({
    values = [],
    onChange,
    translate,
    isImproving = false,
    onImprove,
    onRevert,
    canRevert = false,
    shouldDisableImprove = false,
}) {
    const { isReadOnly } = useContext(CustomAIDimensionContext);
    const [inputValue, setInputValue] = useState('');
    const isInputDisabled = values.length >= MAX_VALUES_LIMIT || isReadOnly || isImproving;

    const formatValue = value => {
        const trimmed = value.trim();
        if (!trimmed) return '';

        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };

    const addValue = () => {
        const formatted = formatValue(inputValue);
        if (!formatted) return;

        if (values.includes(formatted)) {
            toaster.error(translate(TOASTER_TRANSLATIONS.DUPLICATE_VALUE_ERROR), { autoClose: 4000 });
            return;
        }

        onChange([...values, formatted]);
        setInputValue('');
    };

    const handleKeyDown = e => {
        if (e.key !== 'Enter') return;

        e.preventDefault();
        addValue();
    };

    const handleRemoveValue = removedValue => {
        const newValues = values.filter(value => value !== removedValue);
        onChange(newValues);
    };

    return (
        <div className={css.container}>
            <div className={css.valuesContainer}>
                <div className={css.valuesHeader}>
                    <span>
                        <Translate id={FORM_TRANSLATIONS.VALUES_TITLE} />
                    </span>
                    <span>
                        ({values.length}/{MAX_VALUES_LIMIT})
                    </span>
                </div>
                <span className={sharedCss.subtitle}>
                    <Translate id={FORM_TRANSLATIONS.VALUES_SUBTITLE} />
                </span>
                <div className={css.inputRow}>
                    <TextField
                        value={inputValue}
                        onChange={setInputValue}
                        onKeyDown={handleKeyDown}
                        placeholder={FORM_TRANSLATIONS.VALUES_PLACEHOLDER}
                        containerClass={css.inputContainer}
                        inputClassName={sharedCss.textAreaField}
                        disabled={isInputDisabled}
                        maxLength={MAX_OPTIONS_LIMIT}
                    />
                    <Button
                        type="secondary"
                        onClick={addValue}
                        disabled={isInputDisabled || !inputValue.trim()}
                        Icon="plus"
                        iconSize={IconSizes.X_SMALL}
                        text={FORM_TRANSLATIONS.ADD_VALUE_BUTTON}
                    />
                </div>
                {!!values.length && (
                    <CollapsedPanelValues
                        values={values}
                        onDelete={handleRemoveValue}
                        containerClassName={css.collapsedPanel}
                        isReadOnly={isReadOnly || isImproving}
                    />
                )}
                {!isReadOnly && (
                    <ImproveActions
                        isImproving={isImproving}
                        canRevert={canRevert}
                        shouldDisableImprove={shouldDisableImprove}
                        onImprove={onImprove}
                        onRevert={onRevert}
                        improveButtonText={values.length ? IMPROVE_BUTTON_TEXT : AUTO_SUGGEST_BUTTON_TEXT}
                    />
                )}
            </div>
        </div>
    );
}

ValueSourceSection.propTypes = {
    values: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
    isImproving: PropTypes.bool,
    onImprove: PropTypes.func,
    onRevert: PropTypes.func,
    canRevert: PropTypes.bool,
    shouldDisableImprove: PropTypes.bool,
};

export default withLocalize(ValueSourceSection);
